import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Inbox, RefreshCw, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const modeBtn = (active) =>
  `border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
    active ? "border-brand-accent bg-brand-accent text-ink" : "border-white/15 text-muted hover:border-brand-accent hover:text-brand-accent"
  }`;

export default function FairwayTab({ token, onAuthFail }) {
  const [mode, setMode] = useState("outings");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ new: 0, total: 0 });
  const [sponsors, setSponsors] = useState([]);
  const [sponsorCounts, setSponsorCounts] = useState({ new: 0, total: 0 });
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [inq, sp] = await Promise.all([
        axios.get(`${API}/admin/fairway`, { headers }),
        axios.get(`${API}/admin/fairway-sponsors`, { headers }),
      ]);
      setItems(inq.data.inquiries);
      setCounts({ new: inq.data.new, total: inq.data.total });
      setSponsors(sp.data.sponsors);
      setSponsorCounts({ new: sp.data.new, total: sp.data.total });
      setSelected((cur) => {
        if (!cur) return null;
        const list = cur._kind === "sponsor" ? sp.data.sponsors : inq.data.inquiries;
        return list.find((i) => i.id === cur.id) || cur;
      });
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const switchMode = (m) => { setMode(m); setSelected(null); setConfirmDelete(false); };

  const setStatus = async (id, status) => {
    const base = mode === "sponsors" ? "fairway-sponsors" : "fairway";
    try {
      const res = await axios.patch(`${API}/admin/${base}/${id}`, { status }, { headers });
      setSelected({ ...res.data, _kind: mode === "sponsors" ? "sponsor" : "outing" });
      if (mode === "sponsors") setSponsors((l) => l.map((i) => (i.id === id ? res.data : i)));
      else setItems((l) => l.map((i) => (i.id === id ? res.data : i)));
      await load();
      toast.success(status === "reviewed" ? "Marked as reviewed." : "Moved back to new.");
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
      else toast.error("Could not update.");
    }
  };

  const remove = async (id) => {
    const base = mode === "sponsors" ? "fairway-sponsors" : "fairway";
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/${base}/${id}`, { headers });
      toast.success(mode === "sponsors" ? "Sponsor inquiry permanently deleted." : "Fairway response permanently deleted.");
      setSelected(null);
      setConfirmDelete(false);
      await load();
    } catch (e) {
      if (e?.response?.status === 401) onAuthFail();
      else toast.error("Could not delete. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const list = mode === "sponsors" ? sponsors : items;
  const cnt = mode === "sponsors" ? sponsorCounts : counts;

  return (
    <div className="mt-8" data-testid="fairway-tab">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => switchMode("outings")} className={modeBtn(mode === "outings")} data-testid="fairway-mode-outings">
            Outing Inquiries ({counts.total})
          </button>
          <button onClick={() => switchMode("sponsors")} className={modeBtn(mode === "sponsors")} data-testid="fairway-mode-sponsors">
            Sponsor Inquiries ({sponsorCounts.total})
          </button>
        </div>
        <button onClick={load} data-testid="fairway-refresh" className="flex items-center gap-2 border border-white/15 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-muted transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent">
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>
      <p className="mt-3 font-mono text-xs uppercase tracking-[0.25em] text-muted" data-testid="fairway-count">
        {mode === "sponsors" ? "Sponsor Inquiries" : "Fairway Responses"} · {cnt.total} total · <span className="text-brand-accent">{cnt.new} new</span>
      </p>

      <div className="mt-6 grid gap-6 lg:grid-cols-5">
        <div className="lg:col-span-2" data-testid="fairway-list">
          {list.length === 0 ? (
            <div className="grid place-items-center gap-4 border border-white/10 bg-ink-2 py-20 text-center">
              <Inbox className="h-8 w-8 text-white/20" />
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted" data-testid="fairway-empty">
                {mode === "sponsors" ? "No sponsor inquiries yet" : "No Fairway responses yet"}
              </p>
            </div>
          ) : (
            <div data-lenis-prevent className="flex max-h-[70vh] flex-col gap-2 overflow-y-auto overscroll-contain pr-1">
              {list.map((i) => (
                <button
                  key={i.id}
                  onClick={() => { setSelected({ ...i, _kind: mode === "sponsors" ? "sponsor" : "outing" }); setConfirmDelete(false); }}
                  data-testid={`fairway-row-${i.id}`}
                  className={`w-full border p-4 text-left transition-colors duration-300 ${
                    selected?.id === i.id ? "border-brand-accent bg-ink-2" : "border-white/10 bg-ink-2 hover:border-white/30"
                  }`}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="font-display text-base font-extrabold uppercase tracking-tight text-white">
                      {mode === "sponsors" ? i.business_name : i.name}
                    </span>
                    <span className={`px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.2em] ${i.status === "new" ? "bg-brand-accent text-ink" : "bg-white/15 text-white"}`} data-testid={`fairway-row-status-${i.id}`}>
                      {i.status}
                    </span>
                  </div>
                  <p className="mt-1.5 font-mono text-[11px] text-brand-accent">{mode === "sponsors" ? i.budget : i.event_type}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-white/30">{fmtWhen(i.created_at)}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-3">
          {!selected ? (
            <div className="grid h-full min-h-[300px] place-items-center border border-dashed border-white/15 text-center" data-testid="fairway-detail-empty">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-white/40">
                {mode === "sponsors" ? "Select a sponsor inquiry to view the complete submission" : "Select a Fairway response to view the complete inquiry"}
              </p>
            </div>
          ) : (
            <div className="border border-white/10 bg-ink-2 p-7 md:p-9" data-testid="fairway-detail">
              <div className="flex flex-wrap items-center gap-3">
                <span className="bg-brand px-3 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.2em] text-white" data-testid="fairway-detail-type">
                  {selected._kind === "sponsor" ? `Sponsor · ${selected.budget}` : selected.event_type}
                </span>
                <span className="font-mono text-[11px] text-white/40" data-testid="fairway-detail-date">{fmtWhen(selected.created_at)}</span>
              </div>

              <h2 className="mt-4 font-display text-2xl font-extrabold uppercase tracking-tight" data-testid="fairway-detail-name">
                {selected._kind === "sponsor" ? selected.business_name : selected.name}
              </h2>

              <div className="mt-4 grid gap-3 border border-white/10 bg-black/30 p-5 font-mono text-xs sm:grid-cols-2" data-testid="fairway-detail-fields">
                {selected._kind === "sponsor" ? (
                  <>
                    <div><p className="text-white/35">Contact Person</p><p className="mt-1 text-white">{selected.contact_name}</p></div>
                    <div><p className="text-white/35">Budget</p><p className="mt-1 text-white">{selected.budget}</p></div>
                    <div><p className="text-white/35">Email</p><p className="mt-1 text-white">{selected.email}</p></div>
                    <div><p className="text-white/35">Phone</p><p className="mt-1 text-white">{selected.phone}</p></div>
                  </>
                ) : (
                  <>
                    <div><p className="text-white/35">Email</p><p className="mt-1 text-white">{selected.email}</p></div>
                    <div><p className="text-white/35">Phone</p><p className="mt-1 text-white">{selected.phone}</p></div>
                    <div><p className="text-white/35">Event Type</p><p className="mt-1 text-white">{selected.event_type}</p></div>
                    <div><p className="text-white/35">Event Date</p><p className="mt-1 text-white">{selected.event_date}</p></div>
                  </>
                )}
              </div>

              <div className="mt-4 border border-white/10 bg-black/30 p-5" data-testid="fairway-detail-description">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/35">
                  {selected._kind === "sponsor" ? "Description" : "Event Description"}
                </p>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-white/80">{selected.description}</p>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                {selected.status === "new" ? (
                  <button onClick={() => setStatus(selected.id, "reviewed")} data-testid="fairway-mark-reviewed" className="bg-brand-accent px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-white">
                    Mark Reviewed
                  </button>
                ) : (
                  <button onClick={() => setStatus(selected.id, "new")} data-testid="fairway-mark-new" className="border border-white/20 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-muted transition-colors duration-300 hover:border-white/50 hover:text-white">
                    Move to New
                  </button>
                )}
              </div>

              <div className="mt-8 border-t border-white/10 pt-6">
                {!confirmDelete ? (
                  <button
                    onClick={() => setConfirmDelete(true)}
                    data-testid="fairway-delete-button"
                    className="flex items-center gap-2 border border-red-500/40 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-red-400 transition-colors duration-300 hover:border-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete Response
                  </button>
                ) : (
                  <div className="border border-red-500/40 bg-red-500/10 p-5" data-testid="fairway-delete-confirm">
                    <p className="text-sm font-medium text-red-300">
                      Permanently delete this {selected._kind === "sponsor" ? `sponsor inquiry from ${selected.business_name}` : `Fairway Partners Group response from ${selected.name}`}? This cannot be undone.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        onClick={() => remove(selected.id)}
                        disabled={busy}
                        data-testid="fairway-delete-confirm-button"
                        className="bg-red-500 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-red-400 disabled:opacity-60"
                      >
                        {busy ? "Deleting…" : "Yes, Delete Permanently"}
                      </button>
                      <button
                        onClick={() => setConfirmDelete(false)}
                        data-testid="fairway-delete-cancel"
                        className="border border-white/25 px-5 py-3 font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-white/60"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
